import {Theme} from '@mui/material';
import {ConsultPreviewRefundEnum, RefundStatusEnum} from 'domain/usecases';
import {VariantType} from 'presentation/components/IconIllustration/constants';

import IconNamesType from 'presentation/icons/IconNamesType';
import {translations} from 'presentation/translations-new';

const {analysis, approved, cancelled, concluded, denied, registered} =
	translations['pt-br'].privateFlow.consultRefund.status;

const {
	analysisConsultPreview,
	approvedConsultPreview,
	deniedConsultPreview,
	registeredConsultPreview,
} = translations['pt-br'].privateFlow.consultPreviewRefund.status;

type CardStyleByStatusType<T extends string | number | symbol> = {
	[key in T]: {
		iconColor: string;
		chipColor: string;
		borderColor: string;
		headerIcon: {customIconName: IconNamesType; variant: VariantType};
		footer: {
			chipVariant: VariantType;
			chipText: string;
		};
	};
};

export const cardStyleByStatus = (theme: Theme, status: RefundStatusEnum) => {
	const registeredStyle = {
		iconColor: theme.palette.text.secondary,
		borderColor: theme.palette.text.disabled,
		chipColor: theme.palette.customInfo.contrastText,
		headerIcon: {customIconName: 'clipboard-text-clock-outline', variant: 'default'} as const,
		footer: {chipVariant: 'default', chipText: registered} as const,
	};

	const inAnalysisStyle = {
		iconColor: theme.palette.warning.main,
		borderColor: theme.palette.warning.main,
		chipColor: theme.palette.warning.main,
		headerIcon: {customIconName: 'query-builder-filled', variant: 'warning'} as const,
		footer: {chipVariant: 'warning', chipText: analysis} as const,
	};

	const approvedStyle = {
		iconColor: theme.palette.success.dark,
		borderColor: theme.palette.success.dark,
		chipColor: theme.palette.success.dark,
		headerIcon: {customIconName: 'price-clock-filled', variant: 'success'} as const,
		footer: {chipVariant: 'success', chipText: approved} as const,
	};

	const concludedStyle = {
		iconColor: theme.palette.success.dark,
		borderColor: theme.palette.success.dark,
		chipColor: theme.palette.success.dark,
		headerIcon: {customIconName: 'price-check-filled', variant: 'success'} as const,
		footer: {chipVariant: 'success', chipText: concluded} as const,
	};

	const deniedStyle = {
		iconColor: theme.palette.error.dark,
		borderColor: theme.palette.error.dark,
		chipColor: theme.palette.error.dark,
		headerIcon: {customIconName: 'disabled-by-default-filled', variant: 'error'} as const,
		footer: {
			chipVariant: 'error',
			chipText: denied,
		} as const,
	};

	const canceledStyle = {
		iconColor: theme.palette.error.dark,
		borderColor: theme.palette.error.dark,
		chipColor: theme.palette.error.dark,
		headerIcon: {customIconName: 'block-filled', variant: 'error'} as const,
		footer: {
			chipVariant: 'error',
			chipText: cancelled,
		} as const,
	};

	const styles: CardStyleByStatusType<RefundStatusEnum | 'default'> = {
		[RefundStatusEnum.REGISTERED_1]: registeredStyle,
		[RefundStatusEnum.REGISTERED_3]: registeredStyle,
		[RefundStatusEnum.CANCELED_4]: canceledStyle,
		[RefundStatusEnum.DENIED_5]: deniedStyle,
		[RefundStatusEnum.IN_ANALYSIS_6]: inAnalysisStyle,
		[RefundStatusEnum.APPROVED_7]: approvedStyle,
		[RefundStatusEnum.CONCLUDED_8]: concludedStyle,
		default: registeredStyle,
	};

	return styles[status] || styles.default;
};

export const cardStyleByStatusPreview = (theme: Theme, status: ConsultPreviewRefundEnum) => {
	const styles: CardStyleByStatusType<ConsultPreviewRefundEnum | 'default'> = {
		[ConsultPreviewRefundEnum.REGISTERED]: {
			iconColor: theme.palette.text.secondary,
			borderColor: theme.palette.text.disabled,
			chipColor: theme.palette.customInfo.contrastText,
			headerIcon: {customIconName: 'clipboard-text-clock-outline', variant: 'default'},
			footer: {chipVariant: 'default', chipText: registeredConsultPreview},
		},
		[ConsultPreviewRefundEnum.REGISTERED_ALT]: {
			iconColor: theme.palette.text.secondary,
			borderColor: theme.palette.text.disabled,
			chipColor: theme.palette.customInfo.contrastText,
			headerIcon: {customIconName: 'clipboard-text-clock-outline', variant: 'default'},
			footer: {chipVariant: 'default', chipText: registeredConsultPreview},
		},
		[ConsultPreviewRefundEnum.IN_ANALYSIS]: {
			iconColor: theme.palette.warning.main,
			borderColor: theme.palette.warning.main,
			chipColor: theme.palette.warning.main,
			headerIcon: {customIconName: 'query-builder-filled', variant: 'warning'},
			footer: {
				chipVariant: 'warning',
				chipText: analysisConsultPreview,
			},
		},
		[ConsultPreviewRefundEnum.APPROVED]: {
			iconColor: theme.palette.success.dark,
			borderColor: theme.palette.success.dark,
			chipColor: theme.palette.success.dark,
			headerIcon: {customIconName: 'task-alt-filled', variant: 'success'},
			footer: {chipVariant: 'success', chipText: approvedConsultPreview},
		},
		[ConsultPreviewRefundEnum.CANCELED]: {
			iconColor: theme.palette.error.dark,
			borderColor: theme.palette.error.dark,
			chipColor: theme.palette.error.dark,
			headerIcon: {customIconName: 'block-filled', variant: 'error'},
			footer: {
				chipVariant: 'error',
				chipText: cancelled,
			},
		},
		[ConsultPreviewRefundEnum.DENIED]: {
			iconColor: theme.palette.error.dark,
			borderColor: theme.palette.error.dark,
			chipColor: theme.palette.error.dark,
			headerIcon: {customIconName: 'disabled-by-default-filled', variant: 'error'},
			footer: {
				chipVariant: 'error',
				chipText: deniedConsultPreview,
			},
		},
		default: {
			iconColor: theme.palette.text.secondary,
			borderColor: theme.palette.text.disabled,
			chipColor: theme.palette.customInfo.contrastText,
			headerIcon: {customIconName: 'clipboard-text-clock-outline', variant: 'default'},
			footer: {chipVariant: 'default', chipText: registered},
		},
	};

	return styles[status] || styles.default;
};
